/**
=========================================================
* AutoCount - Auto Parts Tracking System
=========================================================

* Based on Material Dashboard 2 PRO React TS by Creative Tim

=========================================================
*/

/**
  All of the routes for AutoCount are defined here.

  Route Configuration:
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav.
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route.
  6. The `icon` key is used for the icon of the route on the Sidenav.
  7. The `collapse` key is used for making a collapsible item with nested routes.
  8. The `route` key is used to store the route location for react router.
  9. The `component` key is used to store the component of its route.
*/

// Dashboard layouts
import AutoCountDashboard from "layouts/autocount/dashboard";

// AutoCount layouts
import PartsList from "layouts/autocount/parts/list";
import PartsAdd from "layouts/autocount/parts/add";
import PartsDetail from "layouts/autocount/parts/detail";
import VehiclesList from "layouts/autocount/vehicles/list";
import VehiclesAdd from "layouts/autocount/vehicles/add";
import ManufacturersList from "layouts/autocount/manufacturers/list";
import InventoryList from "layouts/autocount/inventory/list";
import InventoryScan from "layouts/autocount/inventory/scan";
import InventoryCount from "layouts/autocount/inventory/count";
import ShipmentsList from "layouts/autocount/shipments/list";
import ShipmentsReceive from "layouts/autocount/shipments/receive";
import ShipmentsCreate from "layouts/autocount/shipments/create";
import OrdersList from "layouts/autocount/orders/list";
import OrdersCreate from "layouts/autocount/orders/create";
import OrdersDetail from "layouts/autocount/orders/detail";
import SuppliersList from "layouts/autocount/suppliers/list";
import SuppliersAdd from "layouts/autocount/suppliers/add";
import ReportsInventory from "layouts/autocount/reports/inventory";
import ReportsSales from "layouts/autocount/reports/sales";
import Settings from "layouts/pages/account/settings";

// Authentication
import SignInBasic from "layouts/authentication/sign-in/basic";

// @mui icons
import Icon from "@mui/material/Icon";

const routes = [
  { type: "divider", key: "divider-0" },
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="medium">dashboard</Icon>,
    route: "/dashboard",
    component: <AutoCountDashboard />,
    noCollapse: true,
  },
  { type: "divider", key: "divider-1" },
  { type: "title", title: "Parts Management", key: "parts-management" },
  {
    type: "collapse",
    name: "Parts",
    key: "parts",
    icon: <Icon fontSize="medium">settings_suggest</Icon>,
    collapse: [
      {
        name: "All Parts",
        key: "parts-list",
        route: "/parts",
        component: <PartsList />,
      },
      {
        name: "Add Part",
        key: "parts-add",
        route: "/parts/add",
        component: <PartsAdd />,
      },
      {
        name: "Part Detail",
        key: "parts-detail",
        route: "/parts/:id",
        component: <PartsDetail />,
        hidden: true,
      },
    ],
  },
  {
    type: "collapse",
    name: "Vehicles",
    key: "vehicles",
    icon: <Icon fontSize="medium">directions_car</Icon>,
    collapse: [
      {
        name: "All Vehicles",
        key: "vehicles-list",
        route: "/vehicles",
        component: <VehiclesList />,
      },
      {
        name: "Add Vehicle",
        key: "vehicles-add",
        route: "/vehicles/add",
        component: <VehiclesAdd />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Manufacturers",
    key: "manufacturers",
    icon: <Icon fontSize="medium">factory</Icon>,
    route: "/manufacturers",
    component: <ManufacturersList />,
    noCollapse: true,
  },
  { type: "divider", key: "divider-2" },
  { type: "title", title: "Inventory", key: "inventory-title" },
  {
    type: "collapse",
    name: "Inventory",
    key: "inventory",
    icon: <Icon fontSize="medium">inventory_2</Icon>,
    collapse: [
      {
        name: "Stock Levels",
        key: "inventory-list",
        route: "/inventory",
        component: <InventoryList />,
      },
      {
        name: "Scan Parts",
        key: "inventory-scan",
        route: "/inventory/scan",
        component: <InventoryScan />,
      },
      {
        name: "Cycle Count",
        key: "inventory-count",
        route: "/inventory/count",
        component: <InventoryCount />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Shipments",
    key: "shipments",
    icon: <Icon fontSize="medium">local_shipping</Icon>,
    collapse: [
      {
        name: "All Shipments",
        key: "shipments-list",
        route: "/shipments",
        component: <ShipmentsList />,
      },
      {
        name: "Receive Shipment",
        key: "shipments-receive",
        route: "/shipments/receive",
        component: <ShipmentsReceive />,
      },
      {
        name: "Create Shipment",
        key: "shipments-create",
        route: "/shipments/create",
        component: <ShipmentsCreate />,
      },
    ],
  },
  { type: "divider", key: "divider-3" },
  { type: "title", title: "Sales", key: "sales-title" },
  {
    type: "collapse",
    name: "Orders",
    key: "orders",
    icon: <Icon fontSize="medium">shopping_cart</Icon>,
    collapse: [
      {
        name: "All Orders",
        key: "orders-list",
        route: "/orders",
        component: <OrdersList />,
      },
      {
        name: "Create Order",
        key: "orders-create",
        route: "/orders/create",
        component: <OrdersCreate />,
      },
      {
        name: "Order Detail",
        key: "orders-detail",
        route: "/orders/:id",
        component: <OrdersDetail />,
        hidden: true,
      },
    ],
  },
  {
    type: "collapse",
    name: "Suppliers",
    key: "suppliers",
    icon: <Icon fontSize="medium">business</Icon>,
    collapse: [
      {
        name: "All Suppliers",
        key: "suppliers-list",
        route: "/suppliers",
        component: <SuppliersList />,
      },
      {
        name: "Add Supplier",
        key: "suppliers-add",
        route: "/suppliers/add",
        component: <SuppliersAdd />,
      },
    ],
  },
  { type: "divider", key: "divider-4" },
  { type: "title", title: "Analytics", key: "analytics-title" },
  {
    type: "collapse",
    name: "Reports",
    key: "reports",
    icon: <Icon fontSize="medium">assessment</Icon>,
    collapse: [
      {
        name: "Inventory Report",
        key: "reports-inventory",
        route: "/reports/inventory",
        component: <ReportsInventory />,
      },
      {
        name: "Sales Report",
        key: "reports-sales",
        route: "/reports/sales",
        component: <ReportsSales />,
      },
    ],
  },
  { type: "divider", key: "divider-5" },
  {
    type: "collapse",
    name: "Settings",
    key: "settings",
    icon: <Icon fontSize="medium">settings</Icon>,
    route: "/settings",
    component: <Settings />,
    noCollapse: true,
  },
];

export default routes;
